import { useState, useEffect } from 'react';
import { getDirectionHistory, getUserDirections } from '../../api-services/directionHistory';
import { DirectionChart } from './DirectionChart';

export const DirectionHistory = () => {
    const [directions, setDirections] = useState([]);
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(20);
    const [error, setError] = useState('');
    const [viewMode, setViewMode] = useState('all'); // 'all' or 'personal'
    const [displayMode, setDisplayMode] = useState('table'); // 'table' or 'chart'

    useEffect(() => {
        fetchDirections();
        
        // Auto-refresh every 30 seconds for live updates
        const interval = setInterval(() => {
            fetchDirections();
        }, 30000);
        
        return () => clearInterval(interval);
    }, [page, limit, viewMode, displayMode]);

    const fetchDirections = async () => {
        setError('');
        try {
            let response;
            if (viewMode === 'personal') {
                response = await getUserDirections();
            } else {
                // For chart mode, fetch more data (100 items) for better visualization
                const fetchLimit = displayMode === 'chart' ? 100 : limit;
                response = await getDirectionHistory(page, fetchLimit);
            }

            if (response.ok) {
                const data = await response.json();
                if (viewMode === 'personal') {
                    // Personal directions come as array directly
                    if (Array.isArray(data)) {
                        setDirections(data);
                    }
                } else {
                    // All directions come with success wrapper
                    if (data.success && data.directionHistory) {
                        setDirections(data.directionHistory);
                    }
                }
            } else if (response.status === 401) {
                setError('Unauthorized. Please login again.');
            } else {
                setError('Failed to fetch direction history');
            }
        } catch (error) {
            console.error('Error fetching directions:', error);
            setError('Error fetching direction history');
        }
    };

    const handleViewModeChange = (mode) => {
        setViewMode(mode);
        setPage(1); // Reset to first page when switching modes
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleString('en-US', {
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
        });
    };

    const handleLimitChange = (newLimit) => {
        setLimit(newLimit);
        setPage(1); // Reset to first page when changing limit
    };

    return (
        <div className="bg-slate-800 p-3 rounded-lg border border-slate-700">
            <div className="flex items-center justify-between mb-2">
                <h2 className="text-sm font-semibold text-white flex items-center gap-1.5">
                    <span>📊</span> Direction History
                </h2>
                
                <div className="flex items-center gap-3">
                    {/* View mode toggle */}
                    <div className="flex gap-1 bg-slate-900 rounded-lg p-1">
                        <button
                            onClick={() => handleViewModeChange('all')}
                            className={`px-3 py-1 text-xs font-medium rounded transition-colors ${
                                viewMode === 'all'
                                    ? 'bg-blue-500 text-white'
                                    : 'text-slate-400 hover:text-white'
                            }`}
                        >
                            All
                        </button>
                        <button
                            onClick={() => handleViewModeChange('personal')}
                            className={`px-3 py-1 text-xs font-medium rounded transition-colors ${
                                viewMode === 'personal'
                                    ? 'bg-green-500 text-white'
                                    : 'text-slate-400 hover:text-white'
                            }`}
                        >
                            Personal
                        </button>
                    </div>

                    {/* Display mode toggle */}
                    <div className="flex gap-1 bg-slate-900 rounded-lg p-1">
                        <button
                            onClick={() => setDisplayMode('table')}
                            className={`px-3 py-1 text-xs font-medium rounded transition-colors ${
                                displayMode === 'table'
                                    ? 'bg-purple-500 text-white'
                                    : 'text-slate-400 hover:text-white'
                            }`}
                        >
                            📋 Raw Data
                        </button>
                        <button
                            onClick={() => setDisplayMode('chart')}
                            className={`px-3 py-1 text-xs font-medium rounded transition-colors ${
                                displayMode === 'chart'
                                    ? 'bg-purple-500 text-white'
                                    : 'text-slate-400 hover:text-white'
                            }`}
                        >
                            📈 Graph
                        </button>
                    </div>

                    {/* Items per page selector - only show for 'all' view and table mode */}
                    {viewMode === 'all' && displayMode === 'table' && (
                        <div className="flex items-center gap-2">
                            <span className="text-xs text-slate-400">Show:</span>
                            <select 
                                value={limit}
                                onChange={(e) => handleLimitChange(Number(e.target.value))}
                                className="px-2 py-1 text-xs bg-slate-900 text-white border border-slate-700 rounded outline-none focus:border-blue-500"
                            >
                                <option value={10}>10</option>
                                <option value={20}>20</option>
                                <option value={50}>50</option>
                                <option value={100}>100</option>
                            </select>
                        </div>
                    )}
                </div>
            </div>

            {error && (
                <div className="mb-3 p-2 bg-red-500/10 border border-red-500/20 rounded text-xs text-red-400">
                    {error}
                </div>
            )}

            {/* Direction list with scroll or chart */}
            {displayMode === 'chart' ? (
                directions.length === 0 ? (
                    <div className="bg-slate-900 rounded-lg p-4 text-center text-slate-400 text-sm">
                        No data available for chart
                    </div>
                ) : (
                    <DirectionChart directions={directions} />
                )
            ) : (
                <div className="bg-slate-900 rounded-lg overflow-hidden">
                    <div className="max-h-96 overflow-y-auto">
                        {directions.length === 0 ? (
                            <div className="p-4 text-center text-slate-400 text-sm">
                                No direction history found
                            </div>
                        ) : (
                            <table className="w-full text-xs">
                            <thead className="bg-slate-800 sticky top-0">
                                <tr>
                                    <th className="text-left p-2 text-slate-400 font-medium">ID</th>
                                    {viewMode === 'personal' && (
                                        <th className="text-left p-2 text-slate-400 font-medium">User ID</th>
                                    )}
                                    <th className="text-left p-2 text-slate-400 font-medium">Direction</th>
                                    <th className="text-left p-2 text-slate-400 font-medium">Time Placement</th>
                                    <th className="text-left p-2 text-slate-400 font-medium">Created</th>
                                </tr>
                            </thead>
                            <tbody>
                                {directions.map((dir, index) => (
                                    <tr 
                                        key={dir.id} 
                                        className={`border-t border-slate-800 hover:bg-slate-800/50 ${
                                            index % 2 === 0 ? 'bg-slate-900' : 'bg-slate-900/50'
                                        }`}
                                    >
                                        <td className="p-2 text-slate-300">{dir.id}</td>
                                        {viewMode === 'personal' && (
                                            <td className="p-2 text-slate-400">{dir.user_id}</td>
                                        )}
                                        <td className="p-2">
                                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${
                                                dir.direction === 'UP' 
                                                    ? 'bg-green-500/20 text-green-400 border border-green-500/30' 
                                                    : 'bg-red-500/20 text-red-400 border border-red-500/30'
                                            }`}>
                                                {dir.direction === 'UP' ? '↑' : '↓'} {dir.direction}
                                            </span>
                                        </td>
                                        <td className="p-2 text-slate-300">{dir.time_placement}</td>
                                        <td className="p-2 text-slate-400">{formatDate(dir.created_at)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        )}
                    </div>
                </div>
            )}

            {/* Pagination controls - only for 'all' view and table mode */}
            {displayMode === 'table' && viewMode === 'all' ? (
                <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-700">
                    <button
                        onClick={() => setPage(p => Math.max(1, p - 1))}
                        disabled={page === 1}
                        className={`px-3 py-1 text-xs font-medium rounded transition-colors ${
                            page === 1
                                ? 'bg-slate-700 text-slate-500 cursor-not-allowed'
                                : 'bg-blue-500 text-white hover:bg-blue-600'
                        }`}
                    >
                        ← Previous
                    </button>
                    
                    <span className="text-xs text-slate-400">
                        Page {page} • {directions.length} items
                    </span>
                    
                    <button
                        onClick={() => setPage(p => p + 1)}
                        disabled={directions.length < limit}
                        className={`px-3 py-1 text-xs font-medium rounded transition-colors ${
                            directions.length < limit
                                ? 'bg-slate-700 text-slate-500 cursor-not-allowed'
                                : 'bg-blue-500 text-white hover:bg-blue-600'
                        }`}
                    >
                        Next →
                    </button>
                </div>
            ) : displayMode === 'table' ? (
                <div className="flex items-center justify-center mt-3 pt-3 border-t border-slate-700">
                    <span className="text-xs text-slate-400">
                        Total: {directions.length} personal directions
                    </span>
                </div>
            ) : null}
        </div>
    );
};
